// BuildingOpenings_Pure.ts — PURE engine for the Buildings Set1 opening-binding layer (§3.6).
// No mobx-graphlink / three / DOM / MapObject imports — the Node vitest lane imports this
// directly. The DB-dependent "collect descendant Window/Door mods + resolve their params" half
// lives in `BuildingAccessors.ts`; this file does the segment projection, clamping, fit-check, and
// overlap-check that is identical regardless of source.
import type {BoundOpening, OpeningModelParams, WallSegment} from "./BuildingGeometry.js";
import type {Vec2Simple} from "../../../../Utils/ThreeJS/VectorStructs.js";

/** A candidate opening before binding: the author's object id, kind, its building-local XY anchor
 *  position, and its resolved dimensions (from the fallback chain). `sillHeight` is measured from
 *  `floorTopZ`; doors use 0. `model` carries the P4 resolved model parameters (swing,
 *  swing, pane divisions …), forwarded verbatim onto the bound opening. */
export type OpeningCandidate = {
	objID: string;
	kind: "window" | "door" | "opening";
	/** Building-local XY position of the opening object (the anchor its world position was
	 *  projected to the building frame at). */
	pos: [number, number];
	width: number;
	height: number;
	sillHeight: number;
	/** P4 resolved model parameters. Optional for backward-compat with P2 call sites (tests, older
	 *  accessor code); when absent, `ResolveOpeningsForRoom_Pure` fills harmless defaults. */
	model?: OpeningModelParams;
};

export type ResolveOpeningsForRoom_Result = {
	openings: BoundOpening[];
	warnings: string[];
};

/** Set2 §5: the result of binding a whole FLOOR's opening candidates to the MERGED floor wall
 *  segments. `openingsBySegment` groups by `FloorWallPlan.segments` index (the FLOOR segment list,
 *  not a per-room list — that is the whole point of moving walls to the floor layer). */
export type ResolveOpeningsForFloor_Result = {
	openingsBySegment: Array<{segmentIndex: number, openings: BoundOpening[]}>;
	warnings: string[];
};

/** One floor wall segment as the binder sees it: its INTERIOR face + resolved thickness. Set2 §5
 *  (F1 deviation 2): the binder works on the INTERIOR face, NOT the centerline, because the wall
 *  builder punches the interior face — bind and punch must share one arc length or the hole sits up
 *  to `t/2` off the door/window model at segment ends (the `DoorWallAlignment` bug). The wall
 *  builder (`WallSolidBuilder_FloorSegments`) consumes the SAME `innerLength`, so this binder and
 *  that builder are a matched pair. */
export type FloorWallFace = {
	innerStart: [number, number];
	innerEnd: [number, number];
	/** Length of the interior face — what `alongCenter` is measured against. */
	innerLength: number;
	/** Unit outward normal (same as the merged segment's). */
	normal: [number, number];
	/** The segment's resolved thickness (so a warning can quote it). */
	wallThickness: number;
	/** Rooms bordering this segment (for the "authored under a non-bordering room" warning). */
	claimantRoomIDs: string[];
};

/** A floor-level opening candidate: like `OpeningCandidate` but carrying the id of the Room that
 *  OWNS the authored opening object (the `FindOwningRoomOf` ancestor). Used to warn when an opening
 *  is authored under room A but binds to a wall that does not border A (§5). */
export type FloorOpeningCandidate = OpeningCandidate & {ownerRoomObjID: string|n};

const DEFAULT_JAMB_MARGIN = 0.1; // metres kept free at each end of a segment

/** Project each candidate onto the nearest wall segment of `segments` (the room's building-local
 *  wall segments), per §3.6:
 *  - `alongCenter` = metres from the segment's start along its axis, clamped so the opening stays
 *    fully inside the segment (minus `jambMargin`); if it can't fit, the candidate is dropped.
 *  - `distanceToWall` = the perpendicular offset of the anchor from the segment (positive on the
 *    outward/normal side).
 *  - Openings on the same segment are sorted by `alongCenter` and overlap-checked; an overlapping
 *    (or zero-size) opening is dropped.
 * `zBottom`/`zTop` are computed relative to `floorTopZ` from `sillHeight` + `height`.
 */
export function ResolveOpeningsForRoom_Pure(
	segments: WallSegment[],
	candidates: OpeningCandidate[],
	opts: {jambMargin?: number} = {},
	ownerRoomByObjID?: Map<string, string|n>,
): ResolveOpeningsForRoom_Result {
	const warnings: string[] = [];
	const jambMargin = opts.jambMargin ?? DEFAULT_JAMB_MARGIN;
	if (segments.length == 0) {
		for (const c of candidates) warnings.push(`Opening "${c.objID}" has no room wall to bind to.`);
		return {openings: [], warnings};
	}

	// §11 fix: the jamb margin applies only at segment ends where the wall run truly ENDS.
	// A split sub-span's end that continues into a collinear neighbour (sharing a claimant —
	// e.g. a 1m shared band between two rooms, sandwiched between longer bands of the same
	// rooms) has NO jamb there: a 0.9m door centered on the 1m band is fine because the wall
	// continues on both sides. Compute per-segment per-end continuation flags once.
	const continuesAt = new Map<string, {start: boolean, end: boolean}>();
	for (let i = 0; i < segments.length; i++) {
		const segI = segments[i] as WallSegment & {claimantRoomIDs?: string[]};
		const flags = {start: false, end: false};
		const segLen = segI.length || 1e-9;
		const sdx = (segI.end[0] - segI.start[0]) / segLen, sdy = (segI.end[1] - segI.start[1]) / segLen;
		for (let j = 0; j < segments.length; j++) {
			if (j == i) continue;
			const other = segments[j] as WallSegment & {claimantRoomIDs?: string[]};
			const otherLen = other.length || 1e-9;
			const odx = (other.end[0] - other.start[0]) / otherLen, ody = (other.end[1] - other.start[1]) / otherLen;
			// Collinear (either direction) + shares an endpoint + shares a claimant room.
			if (Math.abs(sdx * ody - sdy * odx) > 1e-6) continue;
			const sharesClaimant = segI.claimantRoomIDs?.some(c=>other.claimantRoomIDs?.includes(c)) ?? false;
			if (!sharesClaimant) continue;
			// The faces sit on the CENTERLINE's offset lines — two collinear centerline sub-spans
		// can carry faces on OPPOSITE sides (each room's interior), so the face segments may be
		// parallel lines a t apart rather than the same line. Continuation = the OTHER segment's
		// centerline passes within t (the union of both half-thicknesses + slack) of this
		// segment's end point, AND that end lies within the other segment's along-span extended
		// by the miter slack.
			const touches = (p: Vec2Simple)=>{
				const ax = other.start[0], ay = other.start[1];
				const dx = other.end[0] - ax, dy = other.end[1] - ay;
				const l2 = dx * dx + dy * dy;
				const tt = l2 ? ((p[0] - ax) * dx + (p[1] - ay) * dy) / l2 : 0;
				// along-span check extended by 0.5m (miter slack — a shared band's mitered end can
			// extend past the centerline junction by up to the thickness difference);
				if (tt < -0.5 || tt > 1.5) return false;
				const cx = ax + tt * dx, cy = ay + tt * dy;
				// perp distance from p to the other segment's LINE — within the combined
				// half-thicknesses of the two bands (unknown here, so a generous 1m cap) + epsilon.
				return Math.hypot(p[0] - cx, p[1] - cy) < 1;
			};
			if (touches(segI.start)) flags.start = true;
			if (touches(segI.end)) flags.end = true;
		}
		continuesAt.set(String(i), flags);
	}

	const bound: BoundOpening[] = [];
	for (const cand of candidates) {
		if (!(cand.width > 0) || !(cand.height > 0)) {
			warnings.push(`Opening "${cand.objID}" has non-positive width/height; dropped.`);
			continue;
		}

		// Find the target segment. Score = TRUE distance from the opening's anchor to each
		// segment (Euclidean to the clamped projection — the along-wall component MATTERS;
		// a perpendicular-only metric puts every collinear wall of a building face in the
		// same 0.1m strip and then array-index decides, which mis-binds corner-adjacent
		// openings onto neighbours' walls). Ties (within epsilon — e.g. an opening exactly
		// at a corner) break toward a segment the opening's owner room claims, then by index.
		const ownerRoom = ownerRoomByObjID?.get(cand.objID) ?? null;
		const TIE_EPSILON = 1e-4; // metres
		let best: {segmentIndex: number, along: number, perpDist: number, euclid: number, ownerClaims: boolean}|n = null;
		for (let i = 0; i < segments.length; i++) {
			const seg = segments[i];
			const proj = ProjectPointOnSegment(cand.pos, seg);
			const pd = proj.perpDist;
			// Euclidean distance from the anchor to its clamped projection on the segment.
			const tangentDist = Math.abs(proj.along - proj.unclampedAlong);
			const euclid = Math.hypot(pd, tangentDist);
			const ownerClaims = ownerRoom != null && (seg as WallSegment & {claimantRoomIDs?: string[]}).claimantRoomIDs?.includes(ownerRoom) === true;
			if (best == null
				|| euclid < best.euclid - TIE_EPSILON
				// within tie-epsilon: owner-claimed segment beats an unclaimed one
				|| (Math.abs(euclid - best.euclid) <= TIE_EPSILON && ownerClaims && !best.ownerClaims)) {
				best = {segmentIndex: i, along: proj.along, perpDist: pd, euclid, ownerClaims};
			}
		}
		const b = best!;
		const seg = segments[b.segmentIndex];

		// Clamp alongCenter so the opening stays fully inside the segment — the jamb margin
		// applies only at ends where the wall run ENDS (§11: not at continuations into a
		// collinear neighbour of the same rooms).
		const halfWidth = cand.width / 2;
		const cont = continuesAt.get(String(b.segmentIndex)) ?? {start: false, end: false};
		const marginStart = cont.start ? 0 : jambMargin;
		const marginEnd = cont.end ? 0 : jambMargin;
		const lo = halfWidth + marginStart;
		const hi = seg.length - halfWidth - marginEnd;
		let alongCenter: number;
		if (hi < lo) {
			if (cand.kind == "opening") {
				// An Opening is meant to "remove this wall": if its width exceeds the segment, just
				// punch the ENTIRE segment (width = the whole wall, centered) instead of dropping.
				// This lets the user author an intentionally-too-high width to wipe out a whole wall.
				bound.push({
					objID: cand.objID,
					kind: cand.kind,
					width: seg.length,
					height: cand.height,
					sillHeight: cand.sillHeight,
					segmentIndex: b.segmentIndex,
					alongCenter: seg.length / 2,
					zBottom: cand.sillHeight,
					zTop: cand.sillHeight + cand.height,
					distanceToWall: Math.abs(b.perpDist),
					model: cand.model ?? DefaultOpeningModel(cand.kind),
				});
				continue;
			}
			warnings.push(`Opening "${cand.objID}" (width ${cand.width.toFixed(2)}m) cannot fit on wall segment ${b.segmentIndex} (length ${seg.length.toFixed(2)}m). Dropped.`);
			continue;
		}
		alongCenter = Math.max(lo, Math.min(b.along, hi));

		bound.push({
			objID: cand.objID,
			kind: cand.kind,
			width: cand.width,
			height: cand.height,
			sillHeight: cand.sillHeight,
			segmentIndex: b.segmentIndex,
			alongCenter,
			zBottom: cand.sillHeight,
			zTop: cand.sillHeight + cand.height,
			distanceToWall: Math.abs(b.perpDist),
			model: cand.model ?? DefaultOpeningModel(cand.kind),
		});
	}

	// Overlap-check: group by segment, sort by alongCenter, drop overlaps.
	bound.sort((a, b)=> a.segmentIndex != b.segmentIndex ? a.segmentIndex - b.segmentIndex : a.alongCenter - b.alongCenter);
	const kept: BoundOpening[] = [];
	for (const op of bound) {
		const sameSeg = kept.filter(k=>k.segmentIndex == op.segmentIndex);
		const overlap = sameSeg.find(k=>OpeningRangesOverlap(k, op));
		if (overlap) {
			warnings.push(`Opening "${op.objID}" overlaps "${overlap.objID}" on wall segment ${op.segmentIndex}. Dropped.`);
			continue;
		}
		kept.push(op);
	}

	return {openings: kept, warnings};
}

/** Set2 §5: bind a WHOLE FLOOR's opening candidates to the MERGED floor wall segments. The
 *  projection math is `ResolveOpeningsForRoom_Pure`'s, unchanged — only the segment LIST differs
 *  (the floor's merged segments instead of one room's ring), so a Door authored under room A on a
 *  shared wall binds to that single merged segment and punches it ONCE (no "other side" authoring
 *  needed — decision 6).
 *
 *  Two behavioural notes:
 *   - `segmentIndex` in the result indexes the FLOOR segment list.
 *   - An opening authored under a room that does NOT border the segment it binds to is warned about
 *     (§5's "stricter than today" note) but still bound — on a floor-level wall list, "on the far
 *     wall" is a legitimate placement rather than a clamp failure.
 *
 *  Runs on the segments' INTERIOR FACES: see `FloorWallFace`. */
export function BindOpeningsToFloorWalls_Pure(
	segments: FloorWallFace[],
	candidates: FloorOpeningCandidate[],
	opts: {jambMargin?: number} = {},
): ResolveOpeningsForFloor_Result {
	const warnings: string[] = [];
	if (segments.length == 0) {
		for (const c of candidates) warnings.push(`Opening "${c.objID}" has no floor wall to bind to.`);
		return {openingsBySegment: [], warnings};
	}
	// Interior-face segments, so `ProjectPointOnSegment` + `length` mean what the wall builder punches.
	// The face segments carry `claimantRoomIDs` (attached below) so the binder's tie-break can
	// prefer the segment the opening's owner room claims (§1a fix: corner-adjacent openings used
	// to be steered onto collinear neighbours' walls by array-index order).
	const faceSegs: WallSegment[] = segments.map(s=>{
		const seg = {
			start: s.innerStart, end: s.innerEnd, length: s.innerLength, normal: s.normal,
		} as WallSegment;
		(seg as WallSegment & {claimantRoomIDs?: string[]}).claimantRoomIDs = s.claimantRoomIDs;
		return seg;
	});

	// Owner map for the tie-break.
	const ownerRoomByObjID = new Map<string, string|n>();
	for (const c of candidates) ownerRoomByObjID.set(c.objID, c.ownerRoomObjID);

	// Reuse the per-segment binder by binding one segment at a time: bind against the FULL floor list
	// (nearest wins) but let the shared routine do the clamping/fit/overlap work by delegating to it
	// with a synthetic index-free pass. Simpler and more auditable than duplicating the math: we call
	// `ResolveOpeningsForRoom_Pure` with the floor's face segments and then RE-INDEX nothing (the
	// indices already refer to the floor list).
	const roomResult = ResolveOpeningsForRoom_Pure(faceSegs, candidates, opts, ownerRoomByObjID);
	warnings.push(...roomResult.warnings);

	// §5 strictness: warn when an opening binds to a segment it does not border.
	const ownerRoomsByIndex = new Map<string, string|n>();
	for (const c of candidates) ownerRoomsByIndex.set(c.objID, c.ownerRoomObjID);
	for (const op of roomResult.openings) {
		const seg = segments[op.segmentIndex];
		const owner = ownerRoomsByIndex.get(op.objID) ?? null;
		if (seg != null && owner != null && !seg.claimantRoomIDs.includes(owner)) {
			warnings.push(`Opening "${op.objID}" is authored under room ${owner} but binds to a wall bordering ${seg.claimantRoomIDs.join("/")}.`);
		}
	}

	// Group by floor-segment index.
	const byIndex = new Map<number, BoundOpening[]>();
	for (const op of roomResult.openings) {
		const arr = byIndex.get(op.segmentIndex) ?? [];
		arr.push(op);
		byIndex.set(op.segmentIndex, arr);
	}
	const openingsBySegment = [...byIndex.entries()]
		.sort((a, b)=>a[0] - b[0])
		.map(([segmentIndex, openings])=>({segmentIndex, openings}));

	return {openingsBySegment, warnings};
}

function OpeningRangesOverlap(a: BoundOpening, b: BoundOpening): boolean {
	const aLo = a.alongCenter - a.width / 2, aHi = a.alongCenter + a.width / 2;
	const bLo = b.alongCenter - b.width / 2, bHi = b.alongCenter + b.width / 2;
	return aLo < bHi && bLo < aHi;
}

/** Harmless defaults for a candidate with no `model` (P2 test / legacy call sites). */
function DefaultOpeningModel(kind: "window" | "door" | "opening"): OpeningModelParams {
	// Real swing value (not the legacy "" placeholder) so it never silently resolves; the door
	// model's ResolveSwing validates strings regardless (bug I).
	if (kind == "opening") return {window: {paneDivisionsX: 0, paneDivisionsY: 0}, door: {swing: "", openAngle: 0}};
	return kind == "window"
		? {window: {paneDivisionsX: 2, paneDivisionsY: 1}, door: {swing: "inRight", openAngle: 0}}
		: {window: {paneDivisionsX: 0, paneDivisionsY: 0}, door: {swing: "inRight", openAngle: 0}};
}

/** Project `point` onto the infinite line through segment `seg`, returning the perpendicular
 *  distance (positive on the outward/normal side), the along-axis distance from `seg.start` for
 *  both the CLAMPED projection (what the punch uses) and the UNCLAMPED line projection (so the
 *  caller can compute a true point-to-segment distance). */
function ProjectPointOnSegment(point: [number, number], seg: WallSegment): {along: number, unclampedAlong: number, perpDist: number} {
	const [px, py] = point;
	const [sx, sy] = seg.start;
	const [ex, ey] = seg.end;
	const dx = ex - sx, dy = ey - sy;
	const len2 = dx * dx + dy * dy;
	const rawT = len2 == 0 ? 0 : ((px - sx) * dx + (py - sy) * dy) / len2;
	const t = Math.max(0, Math.min(1, rawT));
	const projX = sx + t * dx, projY = sy + t * dy;
	// Perpendicular component = (point - projection) · normal.
	const perpDist = (px - projX) * seg.normal[0] + (py - projY) * seg.normal[1];
	return {along: t * Math.sqrt(len2), unclampedAlong: rawT * Math.sqrt(len2), perpDist};
}
